<template>
  <router-link
    v-if="result.source.id"
    :to="route"
    :aria-label="header"
    class="quick-quick-search-result flex flex-column"
  >
    <div
      class="quick-search-top-line flex justify-content-between align-items-center"
    >
      <div class="flex flex-gap-1 align-items-center entity-id">
        <DomainAvatar
          :domain="domainReference"
          :domain-branding="domainBranding"
          size="normal"
        />
        <QuickQuickSearchResponseItemHighlightField
          :value="header"
          field-name="id"
          :highlight="result.highlight"
          class="text-lg font-medium text-gray-darkest"
        />
        <OrderChip
          v-if="isOrder"
          :status="result.source.status as OrderStatus"
        />
        <ShipmentStatusChip
          v-else
          :status="result.source.status as ShipmentStatus"
        />
      </div>
      <router-link v-if="result.source.id" :to="route" class="details-link">
        {{ t('common.viewDetails') }}
        <i aria-hidden="true" class="pi pi-arrow-right" />
      </router-link>
    </div>

    <div
      class="quick-search-middle-line flex align-items-center text-gray-darkest"
    >
      <QuickQuickSearchResponseItemAddress
        v-if="result.source.origin"
        :shipping-point="result.source.origin"
        shipping-point-type="origin"
        :highlight="result.highlight"
      />
      <FontAwesomeIcon
        aria-hidden="true"
        class="text-gray-darker"
        :icon="['fal', 'long-arrow-right']"
      />
      <QuickQuickSearchResponseItemAddress
        v-if="result.source.destination"
        :shipping-point="result.source.destination"
        shipping-point-type="destination"
        :highlight="result.highlight"
      />
    </div>

    <div class="quick-search-bottom-line flex flex-column align-items-start">
      <div
        v-if="referenceNumbers.length"
        class="text-gray-darker reference-number-container"
      >
        <div
          v-for="referenceNumber of referenceNumbers"
          :key="referenceNumber.typeName"
          class="reference-number"
        >
          <span class="reference-number__label">
            {{ referenceNumber.typeName }}:
          </span>
          <QuickQuickSearchResponseItemHighlightField
            class="reference-number__value"
            :value="referenceNumber.values?.join(', ')"
            :field-name="referenceNumber.highlightKey"
            :highlight="result.highlight"
          />
        </div>
      </div>

      <div v-if="tags.length" class="text-gray-darker tags flex">
        <span v-for="tag of tags" :key="tag.tag">
          <mark v-if="tag.highlighted"><Tag :tag="tag.tag" readonly /></mark>
          <Tag v-else :tag="tag.tag" readonly />
        </span>
      </div>
    </div>
  </router-link>
</template>

<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, ref, Ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { DomainBranding, DomainReference } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';
import type { ReferenceNumber as ReferenceNumberSearchResult } from '@rygen/tms-search-model/search/referenceNumber';

import {
  OrderStatus,
  QuickQuickSearchResponseItemOrder,
  QuickQuickSearchResponseItemShipment,
  ShipmentStatus,
} from '@/model';
import { useDomainStore } from '@/stores/Domain';

import OrderChip from '@/components/order/OrderChip.vue';
import QuickQuickSearchResponseItemAddress from '@/components/search/QuickQuickSearchResponseItemAddress.vue';
import QuickQuickSearchResponseItemHighlightField from '@/components/search/QuickQuickSearchResponseItemHighlightField.vue';
import ShipmentStatusChip from '@/components/shipment/ShipmentStatusChip.vue';
import DomainAvatar from '@rygen/rygen-ui/components/domain/DomainAvatar.vue';
import Tag from '@rygen/rygen-ui/components/tag/Tag.vue';

type ReferenceNumberSearchResultHighlights = ReferenceNumberSearchResult & {
  highlight?: boolean;
  highlightKey: string;
  highlightValues?: string[];
};

const props = defineProps<{
  result:
    QuickQuickSearchResponseItemOrder | QuickQuickSearchResponseItemShipment;
}>();
const { result } = toRefs(props);

const { loadDomainBranding, loadDomain } = useDomainStore();
const { t } = useI18n();

const domainBranding: Ref<DomainBranding | undefined> = ref<DomainBranding>();
const hydratedDomain: Ref<DomainReference | undefined> = ref<DomainReference>();

// Use hydrated domain if available, otherwise fallback to naturalId since name is not returned from elastic search
const domainReference = computed(() => {
  if (hydratedDomain.value) {
    return hydratedDomain.value;
  }

  const owner = result.value.source?.owner;
  return owner
    ? new DomainReference({ ...owner, name: owner.naturalId })
    : undefined;
});

const isOrder = computed(() => result.value.type === 'ORDER');
const header = computed(() => {
  return isOrder.value
    ? t('orders.orderNumParam', { param: result.value.source?.id })
    : t('shipments.shipmentNumParam', { param: result.value.source?.id });
});
const route = computed(() => {
  return isOrder.value
    ? { name: 'orderDetail', params: { orderId: result.value.source?.id } }
    : {
        name: 'shipmentDetail',
        params: { shipmentId: result.value.source?.id },
      };
});

const referenceNumbers: Ref<ReferenceNumberSearchResultHighlights[]> = computed(
  () => {
    // these reference number types will always be shown if present, even if they are not highlighted
    const alwaysShowReferenceNumberKeys = [
      'CARRIER_REFERENCE_NUMBER',
      'BILL_OF_LADING_NUMBER',
      'SALES_ORDER_NUMBER',
      'PURCHASE_ORDER_NUMBER',
      'CUSTOMER_REFERENCE_NUMBER',
      'PICKUP_REFERENCE_NUMBER',
      'SHIPPER_ORDER_NUMBER',
    ];

    const highlightedReferenceNumbers = Object.keys(result.value.highlight)
      .filter(
        (highlightKey) =>
          highlightKey.startsWith('referenceNumbers.') ||
          highlightKey.startsWith('relatedReferenceNumbers.') ||
          highlightKey.startsWith('lineItems.referenceNumbers.') ||
          highlightKey.startsWith('childLineItems.referenceNumbers.'),
      )
      .map((highlightKey) => {
        // highlight reference numbers come in the form
        // `referenceNumbers.NATURAL_ID.values: [VALUE_A]` or
        // `relatedReferenceNumbers.NATURAL_ID.values: [VALUE_A]`
        const typeNaturalId = highlightKey
          .replace(/^referenceNumbers\./, '')
          .replace(/^relatedReferenceNumbers\./, '')
          .replace(/^lineItems\.referenceNumbers\./, '')
          .replace(/^childLineItems\.referenceNumbers\./, '')
          .replace(/\.values/, '');
        // if for some reason the result has a highlighted reference number that
        // isn't in the source document, make a faux document
        const fallbackRefNumObject = {
          typeNaturalId,
          typeName: typeNaturalId,
          values: result.value.highlight[typeNaturalId],
        };
        const refNumSource: ReferenceNumberSearchResult =
          result.value.source.referenceNumbers?.[typeNaturalId] ??
          (!isOrder.value
            ? (result.value as QuickQuickSearchResponseItemShipment).source
                .relatedReferenceNumbers?.[typeNaturalId]
            : undefined) ??
          fallbackRefNumObject;
        return {
          ...refNumSource,
          typeNaturalId,
          highlight: true,
          highlightKey,
          highlightValues: result.value.highlight[highlightKey],
        };
      })
      .reduce(
        (agg, curr) => {
          agg[curr.typeNaturalId] = curr;
          return agg;
        },
        {} as Record<string, ReferenceNumberSearchResultHighlights>,
      );

    const refNumsToShow = alwaysShowReferenceNumberKeys
      .map((typeNaturalId) => {
        if (highlightedReferenceNumbers[typeNaturalId]) {
          const highlightedReferenceNumber =
            highlightedReferenceNumbers[typeNaturalId];
          delete highlightedReferenceNumbers[typeNaturalId];
          return highlightedReferenceNumber;
        } else {
          const nonHighlightedReferenceNumber =
            result.value.source.referenceNumbers?.[typeNaturalId];

          return nonHighlightedReferenceNumber
            ? {
                ...nonHighlightedReferenceNumber,
                highlightKey: `referenceNumbers.${typeNaturalId}.values`,
              }
            : undefined;
        }
      })
      .filter(notNull);
    refNumsToShow.push(...Object.values(highlightedReferenceNumbers));

    return refNumsToShow;
  },
);

const tags = computed(() => {
  const highlightedTags = result.value.highlight['tags'] || [];
  const nonHighlightedTags = (result.value.source.tags || []).filter(
    (tag) => !highlightedTags.includes(tag),
  );
  return [
    ...highlightedTags.map((tag) => ({ highlighted: true, tag })),
    ...nonHighlightedTags.map((tag) => ({ highlighted: false, tag })),
  ];
});

watch(
  () => result.value.source.owner?.id,
  async (domainId) => {
    if (!domainId) {
      domainBranding.value = undefined;
      hydratedDomain.value = undefined;
      return;
    }

    const [branding, domain] = await Promise.all([
      loadDomainBranding(domainId),
      loadDomain(domainId),
    ]);

    domainBranding.value = branding.data;
    hydratedDomain.value = domain.data;
  },
  { immediate: true },
);
</script>

<style>
a.quick-quick-search-result {
  --quick-search-background-color: initial;
  --quick-search-link-color: var(--p-primary-500);
  --quick-search-link-text-decoration: none;

  background: var(--quick-search-background-color);
  gap: var(--sp-1);
  border-bottom: 1px solid var(--p-gray-300);
  text-decoration: none;
  max-width: 25vw;

  &:hover {
    --quick-search-background-color: var(--p-surface-200);
    --quick-search-link-color: var(--p-primary-600);
    --quick-search-link-text-decoration: underline;
  }

  .quick-search-top-line {
    .details-link {
      display: flex;
      align-items: center;
      gap: var(--sp-1);
      color: var(--quick-search-link-color);
      text-decoration: var(--quick-search-link-text-decoration);
    }
  }

  .quick-search-middle-line {
    gap: var(--sp-3);
  }

  .quick-search-bottom-line {
    gap: var(--sp-1);

    .reference-number-container {
      width: 100%;

      .reference-number {
        gap: var(--sp-1);
        display: block;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        width: 100%;

        .reference-number__label:after {
          content: ' ';
          white-space: pre;
        }
      }
    }

    .tags {
      gap: var(--sp-1);
      flex-wrap: wrap;

      mark {
        background-color: initial;
        flex: 0 0 auto;

        .rygen-chip.p-chip.rygen-chip-info {
          background-color: var(--p-yellow-200);
        }
      }
    }
  }

  mark {
    background-color: var(--p-yellow-200);
    border-radius: var(--p-border-radius-sm);
    padding: 2px var(--sp-1);
  }
}
</style>
